// frontend/src/pages/CareerLeague.tsx

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { authService } from '../services/auth.service';
import '../styles/career-league.css';

const API_BASE = (import.meta.env.PROD && (import.meta.env.VITE_API_BASE_URL === 'http://localhost:8080' || !import.meta.env.VITE_API_BASE_URL)) ? '' : (import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080');

interface LeagueMember {
  rank: number;
  displayName: string;
  schoolName?: string | null;
  xp: number;
  isCurrentUser: boolean;
}

interface LeagueInfo {
  name: string;
  icon: string;
  min: number;
  max: number | null;
}

interface LeagueData {
  league: LeagueInfo;
  members: LeagueMember[];
}

const LEAGUES = [
  { name: 'Bronz', icon: '🥉', min: 0, max: 1000 },
  { name: 'Gümüş', icon: '🥈', min: 1000, max: 3000 },
  { name: 'Altın', icon: '🥇', min: 3000, max: 6000 },
  { name: 'Platin', icon: '💎', min: 6000, max: 10000 },
  { name: 'Elmas', icon: '👑', min: 10000, max: null }
];

function getAvatarStyle(name: string): string {
  const colors = [
    'linear-gradient(135deg, #4f46e5, #818cf8)',
    'linear-gradient(135deg, #a855f7, #c084fc)',
    'linear-gradient(135deg, #06b6d4, #3b82f6)',
    'linear-gradient(135deg, #ec4899, #f472b6)',
    'linear-gradient(135deg, #10b981, #34d399)'
  ];
  let sum = 0;
  for (let i = 0; i < name.length; i++) {
    sum += name.charCodeAt(i);
  }
  return colors[sum % colors.length];
}

function initials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');
}

export function CareerLeaguePage() {
  const profile = useAuthStore((s) => s.profile);
  const [data, setData] = useState<LeagueData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    authService.getAccessToken().then(async (token) => {
      if (!token) return;
      try {
        const res = await fetch(`${API_BASE}/api/leaderboard/league`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const json = await res.json();
        if (!cancelled) {
          setData(json);
        }
      } catch (err) {
        console.error('Lig verisi alınamadı:', err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  if (isLoading || !profile) {
    return <div className="courses-loading">Yükleniyor...</div>;
  }

  const currentXp = profile.league_xp || 0;

  // Find active league index
  const activeLeagueIndex = LEAGUES.findIndex((l) => {
    if (l.max === null) return currentXp >= l.min;
    return currentXp >= l.min && currentXp < l.max;
  });

  const activeLeague = LEAGUES[activeLeagueIndex] || LEAGUES[0];
  const nextLeague = activeLeagueIndex < LEAGUES.length - 1 ? LEAGUES[activeLeagueIndex + 1] : null;

  // Calculate progress details
  let progressPercent = 0;
  let remainingXp = 0;

  if (activeLeague.max === null) {
    progressPercent = 100;
    remainingXp = 0;
  } else {
    const range = activeLeague.max - activeLeague.min;
    const progress = currentXp - activeLeague.min;
    progressPercent = Math.max(0, Math.min(100, Math.round((progress / range) * 100)));
    remainingXp = activeLeague.max - currentXp;
  }

  // Circular stroke logic
  const circleRadius = 54;
  const circumference = 2 * Math.PI * circleRadius;
  const strokeDashoffset = circumference - (progressPercent / 100) * circumference;

  return (
    <div className="career-league-page">
      <header className="league-header">
        <h1>Kariyer Ligi</h1>
        <p>Aylık lig sezonunda puanınız yalnızca lig skoruna özgüdür; harcanabilir XP’den bağımsızdır.</p>
        <div className="league-header-meta">
          <div className="league-xp-pill">
            <span className="xp-lightning-icon">⚡</span>
            <span className="xp-label">Toplam Lig Puanın:</span>
            <strong className="xp-value">{currentXp.toLocaleString('tr-TR')} XP</strong>
          </div>
          <p className="league-note">Coin alımlarında normal XP azalabilir, ancak lig XP’niz yalnızca aylık sezon sıfırlamasıyla güncellenir.</p>
        </div>
      </header>

      {/* Top Section Cards */}
      <div className="league-top-grid">
        {/* Current League Details Card */}
        <div className="league-main-card">
          <div className="league-main-info">
            <span className="league-main-title-label">Mevcut Lig</span>
            <div className="league-badge-container">
              <div className="league-badge-icon-large">{activeLeague.icon}</div>
              <div>
                <h2 className="league-main-name">{activeLeague.name} Lig</h2>
              </div>
            </div>

            <div className="league-main-stats">
              <div className="league-stat-item">
                <span className="league-stat-item-label">Lig Puanı</span>
                <span className="league-stat-item-value highlight">{currentXp.toLocaleString('tr-TR')} XP</span>
              </div>
              {nextLeague && (
                <div className="league-stat-item">
                  <span className="league-stat-item-label">Sonraki Lig</span>
                  <span className="league-stat-item-value">
                    <span>{nextLeague.icon}</span> {nextLeague.name} Lig
                  </span>
                </div>
              )}
              <div className="league-stat-item">
                <span className="league-stat-item-label">Kalan Süre</span>
                <span className="league-stat-item-value">4 gün</span>
              </div>
            </div>
          </div>

          {/* Circular Loader representation */}
          <div className="league-circle-progress">
            <svg className="circle-svg">
              <circle className="circle-bg" cx="70" cy="70" r={circleRadius} />
              <circle
                className="circle-bar"
                cx="70"
                cy="70"
                r={circleRadius}
                strokeDasharray={circumference}
                strokeDashoffset={strokeDashoffset}
              />
            </svg>
            <div className="circle-text-container">
              <span className="circle-percent">{progressPercent}%</span>
              <span className="circle-label">ilerledi</span>
            </div>
          </div>
        </div>

        {/* Weekly Progress Card */}
        <div className="league-progress-card">
          <div className="league-progress-card-title-row">
            <span>🔥</span> Haftalık İlerleme
          </div>
          {nextLeague ? (
            <>
              <div className="league-progress-card-distance">
                <span className="league-progress-card-distance-label">Lige uzaklık</span>
                <span className="league-progress-card-distance-val">{remainingXp} XP</span>
              </div>
              <div className="progress-bar">
                <div className="progress-bar-fill" style={{ width: `${progressPercent}%` }} />
              </div>
              <div className="league-progress-alert">
                <span className="alert-icon">▲</span>
                <span>4 gün içinde {remainingXp} XP kazanmalısın</span>
              </div>
            </>
          ) : (
            <div className="league-progress-alert" style={{ background: '#ecfdf5', borderColor: '#10b981', color: '#065f46' }}>
              <span className="alert-icon">👑</span>
              <span>En üst ligdesin! Şampiyonluğun tadını çıkar!</span>
            </div>
          )}
          <Link to="/courses" className="btn-xp-gain">
            <span>⚡</span> XP Kazan
          </Link>
        </div>
      </div>

      {/* Leagues Row (Grid layout) */}
      <div className="leagues-row-grid">
        {LEAGUES.map((l, index) => {
          const isCurrent = index === activeLeagueIndex;
          const isUnlocked = index < activeLeagueIndex;
          const isLocked = index > activeLeagueIndex;

          return (
            <div key={l.name} className={`league-row-card ${isCurrent ? 'active' : ''}`}>
              <div className="league-row-card-badge">{l.icon}</div>
              <div className="league-row-card-name">{l.name} Lig</div>
              <div className="league-row-card-xp">{l.min} - {l.max ?? '∞'} XP</div>

              {isCurrent && (
                <button className="btn-league-status current">
                  <span>⭐</span> Mevcut
                </button>
              )}
              {isUnlocked && (
                <button className="btn-league-status unlocked">
                  <span>✓</span> Açık
                </button>
              )}
              {isLocked && (
                <button className="btn-league-status locked">
                  <span>🔒</span> Kilitli
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Bottom Rankings & Rewards Sections */}
      <div className="league-bottom-grid">
        {/* Same League Rankings List */}
        <div className="league-rankings-box">
          <div className="league-section-title-row">
            <span>🏆</span> {activeLeague.name} Lig Sıralaması
          </div>
          {data && data.members ? (
            <div className="league-rankings-list">
              {data.members.map((m) => {
                const rankCls = m.rank === 1 ? 'gold' : m.rank === 2 ? 'silver' : m.rank === 3 ? 'bronze' : 'normal';
                return (
                  <div key={m.rank} className={`league-rankings-item ${m.isCurrentUser ? 'current-user' : ''}`}>
                    <div className={`league-rankings-rank ${rankCls}`}>
                      {m.rank === 1 ? '🏆' : m.rank === 2 ? '🥈' : m.rank === 3 ? '🥉' : m.rank}
                    </div>
                    <div
                      className="league-rankings-avatar"
                      style={{ background: getAvatarStyle(m.displayName) }}
                    >
                      {initials(m.displayName)}
                    </div>
                    <div className="league-rankings-info">
                      <h5>
                        {m.displayName}
                        {m.isCurrentUser && <span className="current-tag">(Sen)</span>}
                      </h5>
                      {m.schoolName && <p style={{ fontSize: 11, color: 'var(--gray)', margin: 0 }}>{m.schoolName}</p>}
                    </div>
                    <div className="league-rankings-xp">
                      {m.xp.toLocaleString('tr-TR')} XP
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="courses-loading">Yükleniyor...</div>
          )}
        </div>

        {/* League Rewards List */}
        <div className="league-rewards-box">
          <div className="league-section-title-row">
            <span>🏅</span> Lig Ödülleri
          </div>
          <div className="league-rewards-list">
            <div className="league-reward-card">
              <div className="league-reward-info-left">
                <span className="league-reward-title">🏅 Altın Lig</span>
                <p className="league-reward-desc">
                  Nadir rozetler • Premium avatarlar • AI mentor önceliği • Haftalık 2x XP
                </p>
              </div>
              <span className={`league-reward-status-right ${activeLeagueIndex >= 2 ? 'active' : ''}`}>
                {activeLeagueIndex >= 2 ? 'Kazanıldı' : `Kalan: ${remainingXp} XP`}
              </span>
            </div>

            <div className="league-reward-card">
              <div className="league-reward-info-left">
                <span className="league-reward-title">⭐ Özel Temalar</span>
                <p className="league-reward-desc">Profiliniz için koyu, neon ve uzay temaları</p>
              </div>
              <span className={`league-reward-status-right ${activeLeagueIndex >= 1 ? 'active' : ''}`}>
                {activeLeagueIndex >= 1 ? 'Kazanıldı' : 'Gümüş Ligde Açılır'}
              </span>
            </div>

            <div className="league-reward-card">
              <div className="league-reward-info-left">
                <span className="league-reward-title">✨ Haftalık Bonus XP</span>
                <p className="league-reward-desc">Her hafta sonu tamamladığınız derslerden +50% XP bonusu</p>
              </div>
              <span className={`league-reward-status-right ${activeLeagueIndex >= 1 ? 'active' : ''}`}>
                {activeLeagueIndex >= 1 ? 'Kazanıldı' : 'Gümüş Ligde Açılır'}
              </span>
            </div>

            <div className="league-reward-card">
              <div className="league-reward-info-left">
                <span className="league-reward-title">👑 Elmas Lig VIP</span>
                <p className="league-reward-desc">En üst ligde özel fiziksel ödüller ve erken erişim hakkı!</p>
              </div>
              <span className="league-reward-status-right">
                {activeLeagueIndex >= 4 ? 'Aktif' : 'Elmas Ligde Açılır'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <section className="league-how-section">
        <div className="league-section-title-row" style={{ marginBottom: 8 }}>
          <span>⚙️</span> Lig Sistemi Nasıl Çalışır?
        </div>
        <div className="league-how-grid">
          <div className="league-how-card">
            <div className="league-how-card-icon">📝</div>
            <div className="league-how-card-title">Ders Tamamla</div>
            <div className="league-how-card-desc">Her ders = 10-30 XP</div>
          </div>
          <div className="league-how-card">
            <div className="league-how-card-icon">✅</div>
            <div className="league-how-card-title">Quiz Çöz</div>
            <div className="league-how-card-desc">Doğru cevap = 5-15 XP</div>
          </div>
          <div className="league-how-card">
            <div className="league-how-card-icon">🎯</div>
            <div className="league-how-card-title">Proje Bitir</div>
            <div className="league-how-card-desc">Tam proje = 50-200 XP</div>
          </div>
          <div className="league-how-card">
            <div className="league-how-card-icon">🔥</div>
            <div className="league-how-card-title">Seri Tut</div>
            <div className="league-how-card-desc">Her gün = +20 XP bonus</div>
          </div>
        </div>
      </section>
    </div>
  );
}
